import { ReactElement, useMemo } from "react";
import { Link } from "react-router-dom";
import Button from "@aleaplay/web-components/ui/Button";
import Table from "@aleaplay/web-components/ui/Table";

import { useTranslation } from "@/v2/hooks/useTranslation";
import { FreeSpinsSyncResult } from "@/v2/types/FreeSpinsSync/FreeSpinsSyncResult.interface";

import { useDownloadFailedGamesCsv } from "../hooks/useDownloadFailedGamesCsv";
import { useFailuresSummary } from "../hooks/useFailuresSummary";
import { getFailedGames } from "../utils/getFailedGames";

import { FreeSpinsRetrievalPanel } from "./FreeSpinsRetrievalPanel";

/** Counters, the failed games table and the CSV download. */
export const FreeSpinsRetrievalFailures = (
  props: FreeSpinsRetrievalFailuresProps,
): ReactElement => {
  const { result, targetName, showGamesTable = true } = props;
  const { t } = useTranslation();

  const failedGames = useMemo(() => getFailedGames(result.failures), [result.failures]);
  const hasFailedGames = failedGames.length > 0;
  // A single-game retrieval already names its game: the table would only repeat it.
  const hasGamesTable = hasFailedGames && showGamesTable;
  // Names everything that failed, so the panel explains the list below it.
  const summary = useFailuresSummary(result);

  // Nothing published: every game failed.
  const hasEveryGameFailed = result.valuesPublished === 0;

  const handleDownload = useDownloadFailedGamesCsv({ failures: result.failures, targetName });

  return (
    <>
      <FreeSpinsRetrievalPanel type={hasEveryGameFailed ? "error" : "warning"}>
        {summary}
      </FreeSpinsRetrievalPanel>
      <div className="fs-retrieval__failures-header">
        {hasGamesTable ? (
          <h3>{t("free-spin.fs-retrieval-modal.failed-games", { count: failedGames.length })}</h3>
        ) : (
          // Failures without a game are only in the download.
          <p className="fs-retrieval__download-note" data-testid="fs-retrieval-download-note">
            {t("free-spin.fs-retrieval-modal.download-details")}
          </p>
        )}
        <Button
          modifier="secondary"
          onClick={handleDownload}
          data-testid="fs-retrieval-download-button"
        >
          {t("free-spin.fs-retrieval-modal.download")}
        </Button>
      </div>
      {hasGamesTable && (
        <div className="fs-retrieval__failures" data-testid="fs-retrieval-failures">
          <Table>
            <Table.Header>{t("free-spin.fs-retrieval-modal.game-id")}</Table.Header>
            <Table.Header>{t("free-spin.fs-retrieval-modal.game-code")}</Table.Header>
            {failedGames.map(({ gameId, gameCode }) => (
              <Table.Row key={gameId}>
                <Table.Column>
                  {/* A new tab keeps the result open. */}
                  <Link to={`/games/${gameId}`} target="_blank" rel="noopener noreferrer">
                    {gameId}
                  </Link>
                </Table.Column>
                <Table.Column>{gameCode}</Table.Column>
              </Table.Row>
            ))}
          </Table>
        </div>
      )}
    </>
  );
};

interface FreeSpinsRetrievalFailuresProps {
  result: FreeSpinsSyncResult;
  /** whether to list the failed games; off when only one game was retrieved */
  showGamesTable?: boolean;
  /** Names the downloaded file. */
  targetName: string;
}
